import axios, { AxiosResponse } from 'axios';
import { action, atom, onMount } from 'nanostores';
import {
  AnalyzeCsvRequest,
  AnalyzeCsvResponse,
  AudioFile,
  Configurations,
  ExternalSourceSync,
  Project,
  ProjectStatus,
  ProjectsService,
  ProvisionProjectRequest,
  ProvisionProjectUsingAudioFilesRequest,
  ProvisionProjectUsingExternalDataSourceRequest,
  SmartAnalyzeService,
  SyncStatus,
} from '../api';
import { IntegrationType } from '../api/models/IntegrationType';

/**
 * Loader state while loading projects
 */
export const $loadingProjects = atom<boolean>(false);

/**
 * The currently selected project.
 */
export const $project = atom<Project | undefined>();

/**
 * Result of the currently selected project
 */
export const $projectResult = atom<string>('');

/**
 * The set of projects in the current scope (org/workspace)
 */
export const $projects = atom<Project[]>([]);

/**
 * The set of projects in the current scope (org/workspace)
 */
export const $externalSourceSyncStatuses = atom<ExternalSourceSync[]>([]);

/**
 * We use this to display whether the file has been downloaded.
 */
export const $downloaded = atom<string>('');

/**
 * When the projects store is mounted, we'll connect events for
 * real-time notifications.
 */
onMount($projects, () => {
  signalRService.register('UpdateProjectStatus', (msg: string) => {
    const [projectId, status] = msg.split('=');

    updateProjectField(projectId, 'status', status as ProjectStatus);
  });

  signalRService.register('CompletedRows', (msg: string) => {
    const [projectId, rowCount] = msg.split('=');

    updateProjectField(projectId, 'processedRows', Number.parseInt(rowCount));
  });

  signalRService.register('UpdateExternalDataImportStatus', (msg: string) => {
    const [projectId, status] = msg.split('=');

    updateProjectExternalSourceSyncFields(
      projectId,
      'lastSyncStatus',
      status as SyncStatus,
    );
  });

  signalRService.register('CompletedExternalDataImportRows', (msg: string) => {
    const [projectId, importedRecords, totalRecords] = msg.split('=');

    updateProjectField(projectId, 'totalRows', Number.parseInt(totalRecords));

    updateProjectExternalSourceSyncFields(
      projectId,
      'totalRecords',
      totalRecords as unknown as number,
    );

    updateProjectExternalSourceSyncFields(
      projectId,
      'importedRecords',
      importedRecords as unknown as number,
    );
  });
});

/**
 * Main function to load projects.
 */
const loadProjects = async (organizationId: string, workspaceId: string) => {
  console.log('Retrieving projects...');
  $loadingProjects.set(true); // Set loading state to true

  await ensureOrganization(organizationId);
  await ensureWorkspace(workspaceId);

  var projects = await ProjectsService.getProjects(workspaceId);

  $projects.set(projects);

  // also fetch the source syncs statuses
  var externalsyncs =
    await ProjectsService.externalSourceSyncStatus(workspaceId);
  $externalSourceSyncStatuses.set(externalsyncs);

  $loadingProjects.set(false); // Set loading state back to false
};

/**
 * Reload projects on workspace change
 */
$workspace.subscribe(async ws => {
  $projects.set([]);

  const params = $router.get()?.params as Record<'organizationId', string>;

  if (
    params &&
    params.organizationId &&
    params.organizationId.trim().length > 0 &&
    ws
  ) {
    await loadProjects(params.organizationId, ws?.workspace.id ?? '');
  }
});

/**
 * Watch for route changes; if we haven't loaded the projects, we try to load them.
 */
route.subscribe(async name => {
  if (name !== 'projects' || ($projects.value?.length ?? 0) > 0) {
    return;
  }

  const params = $router.get()?.params as Record<'organizationId', string> &
    Record<'workspaceId', string>;

  await loadProjects(params.organizationId, params.workspaceId);
});

/**
 * Watch for route changes; load the current selected project result.
 */
route.subscribe(async name => {
  if (name !== 'project') {
    return;
  }

  const params = $router.get()?.params as Record<'organizationId', string> &
    Record<'workspaceId', string> &
    Record<'projectId', string>;

  await loadProjectResult(
    params.organizationId,
    params.workspaceId,
    params.projectId,
  );
});

/**
 * Main function to load project result.
 */
const loadProjectResult = async (
  organizationId: string,
  workspaceId: string,
  projectId: string,
) => {
  console.log('Retrieving project result...');

  await ensureOrganization(organizationId);
  await ensureWorkspace(workspaceId);
  await ensureProject(projectId);
  var projectResult = await ProjectsService.downloadResults(projectId);

  $projectResult.set(projectResult);
};

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
export const createProject = action(
  $project,
  'createProject',
  async (
    store,
    name: string,
    orgId: string,
    workspaceId: string,
    file: File,
    agents: string[],
    configurations: Configurations,
  ) => {
    const request: ProvisionProjectRequest = {
      name: name,
      orgId: orgId,
      workspaceId: workspaceId,
      fileName: file.name,
      fileContentsBase64: await file2Base64(file),
      agents: agents,
      configurations,
    };

    const response = await ProjectsService.provisionProject(request);

    if (!response.project) {
      // error is displayed by caller
      return response;
    }

    $project.set(response.project);

    $projects.set([response.project, ...$projects.get()]);
    return response;
  },
);

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
export const createProjectWithExternalDataSource = action(
  $project,
  'createProjectWithExternalDataSource',
  async (
    store,
    name: string,
    orgId: string,
    workspaceId: string,
    agents: string[],
    externalDataSource: IntegrationType,
    startDate: string,
    endDate: string,
    configurations: Configurations,
  ) => {
    const request: ProvisionProjectUsingExternalDataSourceRequest = {
      name: name,
      orgId: orgId,
      workspaceId: workspaceId,
      agents: agents,
      externalDataSource: externalDataSource,
      startDate: startDate,
      endDate: endDate,
      configurations,
    };

    const response =
      await ProjectsService.provisionProjectUsingExternalDataSource(request);

    if (!response.project) {
      // TODO: Show error
      return;
    }

    $project.set(response.project);

    $projects.set([response.project, ...$projects.get()]);

    // also fetch the source syncs statuses
    var externalsyncs =
      await ProjectsService.externalSourceSyncStatus(workspaceId);
    $externalSourceSyncStatuses.set(externalsyncs);
  },
);

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
export const createProjectWithAudioFiles = action(
  $project,
  'createProjectWithAudioFiles',
  async (
    store,
    name: string,
    orgId: string,
    workspaceId: string,
    files: File[],
    agents: string[],
    configurations: Configurations,
  ) => {
    const audioFiles = await Promise.all(
      files.map(async file => {
        const base64File = await file2Base64(file);
        return {
          fileName: file?.name,
          fileContentsBase64: base64File,
          extension: file?.name?.split('.').pop() ?? 'mp3',
        } as AudioFile;
      }),
    );

    const request: ProvisionProjectUsingAudioFilesRequest = {
      name: name,
      orgId: orgId,
      workspaceId: workspaceId,
      audioFiles,
      agents: agents,
      configurations,
    };

    const response =
      await ProjectsService.provisionProjectUsingAudioFiles(request);

    if (!response.project) {
      // error is displayed by caller
      return response;
    }

    $project.set(response.project);

    $projects.set([response.project, ...$projects.get()]);
    return response;
  },
);

/**
 * Starts the processing on the project by making an API call to set the
 * status to ready.  Locally, the service will pick this up automatically.
 * In production, we'll kick off the processing job.
 */
export const runProject = action(
  $project,
  'runProject',
  async (store, projectId: string) => {
    await ProjectsService.runProject(projectId);

    // The initial state is ready.
    updateProjectField(projectId, 'status', 'Ready');
  },
);

/**
 * Cancels the processing on the project by making an API call to set the
 * status to ready.  Locally, the service will pick this up automatically.
 * In production, we'll kick off the processing job.
 */
export const cancelProject = action(
  $project,
  'cancelProject',
  async (store, projectId: string) => {
    await ProjectsService.cancelProject(projectId);

    // The initial state is ready.
    updateProjectField(projectId, 'status', 'Cancelled');
  },
);

/**
 * Delete's the selected project.
 */
export const deleteProject = action(
  $project,
  'deleteProject',
  async (store, projectId: string) => {
    await ProjectsService.deleteProject(projectId);

    $project.set(undefined);

    // Splice out the project
    const projects = $projects.get();

    const index = projects.findIndex(p => p.id === projectId);

    if (index > -1) {
      projects.splice(index, 1);

      $projects.set(projects);
    }
  },
);

/**
 * Updates the status of the specified project.
 * @param projectId The ID of the project to update.
 * @param status The status to set for the project.
 */
function updateProjectField<K extends keyof Project>(
  projectId: string,
  field: K,
  value: Project[K],
) {
  if ($project.get()?.id === projectId) {
    const project = $project.get();

    if (project) {
      project[field] = value;

      if (project) {
        $project.set(project);
      }
    }
  }

  // Update the projects array
  var projects = $projects.get();

  const index = projects.findIndex(p => p.id === projectId);

  projects[index][field] = value;

  $projects.set([...projects]);
}

/**
 * Updates the status of the specified project.
 * @param projectId The ID of the project to update.
 * @param status The status to set for the project.
 */
function updateProjectExternalSourceSyncFields<
  K extends keyof ExternalSourceSync,
>(projectId: string, field: K, value: ExternalSourceSync[K]) {
  // Update the projects array
  var syncStatuses = $externalSourceSyncStatuses.get();

  const index = syncStatuses.findIndex(s => s.project.id === projectId);

  syncStatuses[index][field] = value;

  $externalSourceSyncStatuses.set([...syncStatuses]);
}

/**
 * Trigger the download of the results as a CSV.
 */
export const downloadProjectResults = action(
  $project,
  'downloadProjectResults',
  async (store, projectId: string) => {
    axios(`${apiUrl}/projects/${projectId}/downloadresults`, {
      headers: {
        Authorization: `Bearer ${(await $user.get()?.getIdToken(true)) ?? ''}`,
      },
    }).then((response: AxiosResponse<any, any>) => {
      const disposition = response.headers['content-disposition'] as string;

      const filename = disposition.match(/filename=([^;]+)/)?.[1];

      const blob = new Blob([response.data], { type: 'text/plain' });
      // create file link in browser's memory
      const href = URL.createObjectURL(blob);

      // create "a" HTML element with href to file & click
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', filename ?? `${projectId}-results.csv`);
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      $downloaded.set(projectId);

      window.setTimeout(() => $downloaded.set(''), 3000);
    });
  },
);

/**
 * Encode the download link to Base64 and send it to the SmartAnalyzer.
 */
export const sendDownloadLinkToSmartAnalyzer = action(
  $project,
  'sendDownloadLinkToSmartAnalyzer',
  async (store, projectId: string) => {
    try {
      const response = await axios(
        `${apiUrl}/projects/${projectId}/downloadresults`,
        {
          headers: {
            Authorization: `Bearer ${(await $user.get()?.getIdToken(true)) ?? ''}`,
          },
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], { type: 'text/csv' });
      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result;

        if (typeof result === 'string') {
          const base64data = result.split(',')[1];
          if (base64data) {
            const smartAnalyzerUrl = `/smartanalyze?csv=${encodeURIComponent(base64data)}`;

            // Redirect to the SmartAnalyzer app
            window.location.href = smartAnalyzerUrl;
          } else {
            console.error('Failed to extract Base64 data.');
          }
        } else {
          console.error('Unexpected result type:', result);
        }
      };

      reader.onerror = error => {
        console.error('Failed to read the blob data:', error);
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to download the project results:', error);
    }
  },
);

/**
 * Encode the download link to Base64 and send it to the SmartAnalyzer.
 */
export const smartAnalyzeCsv = action(
  $project,
  'smartAnalyzeCsv',
  async (store, orgId: string, projectId?: string, file?: File) => {
    let newFile: File | undefined = undefined;
    if (projectId && !file) {
      const response = await axios(
        `${apiUrl}/projects/${projectId}/downloadresults`,
        {
          headers: {
            Authorization: `Bearer ${(await $user.get()?.getIdToken(true)) ?? ''}`,
          },
          responseType: 'blob',
        },
      );

      const csvText = await response.data.text();
      const rowCount = csvText.split('\n').length - 1;
      if (rowCount > 1000) {
        throw new Error('File exceeds 1000 rows limit. Please filter your data and try again.');
      }

      newFile = new File([response.data], 'filename.csv', {
        type: 'text/csv',
      });
    } else if (!projectId && file) {
      const text = await file.text();
      const rowCount = text.split('\n').length - 1;
      if (rowCount > 1000) {
        throw new Error('File exceeds 1000 rows limit. Please filter your data and try again.');
      }
      
      newFile = file;
    }

    const request: AnalyzeCsvRequest = {
      fileContentsBase64: await file2Base64(newFile!),
      orgId,
    };

    const analyzeCsvResponse: AnalyzeCsvResponse =
      await SmartAnalyzeService.analyzeCsv(request);

    return analyzeCsvResponse;
  },
);

/**
 * Ensure that the project is loaded and selected.
 */
export const ensureProject = action(
  $project,
  'ensureProject',
  async (store, projectId: string) => {
    let project: Project | undefined;

    const params = $router.get()?.params as Record<'workspaceId', string>;

    if ($projects.get().length === 0) {
      const projects = await ProjectsService.getProjects(
        params.workspaceId ?? '',
      );

      $projects.set(projects);

      project = projects.find(p => p.id === projectId);
    } else {
      project = $projects.get().find(p => p.id === projectId);
    }

    $project.set(project);
  },
);

// https://stackoverflow.com/a/65586375/116051
const file2Base64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      resolve(
        reader.result?.toString().replace('data:', '').replace(/^.+,/, '') ||
          '',
      );
    reader.onerror = error => reject(error);
  });
};
